/**
 * LA SOURCE UNIQUE DES ROUTES DU FRONT.
 *
 * Trois lecteurs, un seul fichier :
 *   1. le routeur, qui monte un écran par entrée ;
 *   2. `screens.tsx`, dont le type oblige à fournir un écran pour CHAQUE entrée ;
 *   3. la construction, qui en tire `routes.manifest.json` et permet au
 *      déploiement du backend de refuser une entrée de menu menant nulle part
 *      (décision D17).
 *
 * CE FICHIER N'IMPORTE RIEN À L'EXÉCUTION, et ce n'est pas un hasard : le
 * greffon Vite le transpile et l'évalue hors du navigateur pour écrire le
 * manifeste. Un import de composant ici et l'évaluation tirerait tout React
 * derrière elle. Les types se déclarent donc avec `import type`, qu'esbuild
 * efface.
 *
 * Les routes paramétrées (`/students/:id`) sont admises ici mais n'ont pas leur
 * place dans une entrée de menu — le menu n'a aucun identifiant à y mettre — et
 * `MenuSpec` les refuse côté backend.
 */

export interface RouteDeclaration {
    /** Le chemin, tel qu'il apparaît dans `menu_item.route`. */
    readonly path: string;
    /** La clé de l'écran monté sur ce chemin. Voir `screens.tsx`. */
    readonly screen: string;
}

/**
 * PAS DE TITRE ICI, volontairement. Le titre d'un écran est le libellé de son
 * entrée de menu, qui appartient à l'établissement (D19) et arrive traduit dans
 * `/api/me`. En déclarer un second dans le front donnerait deux vérités pour un
 * même écran, et celle du front gagnerait sans avoir été choisie par personne.
 */

export const ROUTES = [
    { path: '/', screen: 'home' },
    { path: '/administration/users', screen: 'users' },
    // Routes sans entrée de menu : on y arrive depuis la liste. `new` est
    // statique et `:id` paramétrée — react-router classe la première avant la
    // seconde, l'ordre ici n'y change rien.
    { path: '/administration/users/new', screen: 'userCreate' },
    { path: '/administration/users/:id', screen: 'userDetail' },
    // Le changement VOLONTAIRE de son mot de passe (D30). Le changement IMPOSÉ
    // (D32) ne passe pas par ici : il remplace toute la coquille.
    { path: '/account/password', screen: 'changePassword' },
    // Le profil du compte connecté : sa photo, ses informations, son mot de
    // passe. Ouvert par le nom en haut à droite ; aucune permission requise.
    { path: '/account', screen: 'profile' },
    { path: '/administration/roles', screen: 'roles' },
    // Attribuer un rôle, et modifier une attribution (D35) : on y arrive depuis
    // la liste, le catalogue ou la fiche d'un compte.
    { path: '/administration/roles/assign', screen: 'assignmentCreate' },
    { path: '/administration/roles/assignments/:id', screen: 'assignmentEdit' },
    { path: '/administration/identity-providers', screen: 'identityProviders' },
    { path: '/administration/authentication-log', screen: 'authenticationLog' },
    // D36 : réservé au Super Administrateur (permission role.manage). Le menu
    // de l'établissement ne mène jamais ici.
    { path: '/super-administration/roles-menus', screen: 'rolesMenus' },
    // D45 : la configuration du référentiel (referential.configure). Le menu
    // principal s'y replie par défaut (D46).
    { path: '/referential/configuration', screen: 'referentialConfiguration' },
    // D47 : le catalogue des formations (training.read). Une formation et sa
    // saisie en masse s'ouvrent depuis la liste.
    { path: '/referential/trainings', screen: 'trainings' },
    { path: '/referential/trainings/:id', screen: 'training' },
    { path: '/referential/trainings/:id/bulk', screen: 'trainingBulk' },
    // D48 : la scolarité (student.read). La fiche, l'assistant d'inscription et
    // la réinscription d'une promotion s'ouvrent depuis les listes.
    { path: '/schooling/students', screen: 'students' },
    { path: '/schooling/students/:id', screen: 'student' },
    { path: '/schooling/enrolments', screen: 'enrolments' },
    { path: '/schooling/enrolments/new', screen: 'enrol' },
    { path: '/schooling/enrolments/reenrol', screen: 'reenrol' },
    { path: '/schooling/classes', screen: 'classes' },
    // D51 : la finance (finance.read), et ses réglages réservés au Super
    // Administrateur (finance.configure). Le compte d'un étudiant vit dans sa
    // fiche, pas sur une route à lui.
    { path: '/finance/dashboard', screen: 'financeDashboard' },
    { path: '/finance/invoices', screen: 'financeInvoices' },
    { path: '/finance/prices', screen: 'financePrices' },
    { path: '/finance/services', screen: 'financeServices' },
    { path: '/finance/discounts', screen: 'financeDiscounts' },
    { path: '/finance/schedules', screen: 'financeSchedules' },
    // D68 : le panneau des modules (planche 154) ; les réglages de la Finance
    // s'ouvrent depuis sa carte.
    { path: '/super-administration/modules', screen: 'modules' },
    { path: '/super-administration/modules/finance', screen: 'financeSettings' },
    // D55 : les évaluations et les notes. « Mes évaluations » montre celles des
    // matières de l'enseignant connecté ; une évaluation s'ouvre depuis l'une
    // ou l'autre liste.
    { path: '/assessment/evaluations', screen: 'assessments' },
    { path: '/assessment/evaluations/:id', screen: 'assessment' },
    { path: '/assessment/mine', screen: 'myAssessments' },
    { path: '/assessment/mine/:id', screen: 'assessment' },
    // D57 : le règlement des études. Une version, un profil, la simulation
    // s'ouvrent depuis la liste ; la règle effective et l'explication d'une
    // moyenne depuis une version ou la fiche d'un étudiant.
    { path: '/assessment/regulation', screen: 'regulationVersions' },
    { path: '/assessment/regulation/effective', screen: 'regulationEffective' },
    { path: '/assessment/regulation/explanation', screen: 'regulationExplanation' },
    { path: '/assessment/regulation/profiles/:id', screen: 'regulationProfile' },
    { path: '/assessment/regulation/:id', screen: 'regulationVersion' },
    { path: '/assessment/regulation/:id/simulation', screen: 'regulationSimulation' },
    // D58 : les délibérations.
    { path: '/assessment/deliberations', screen: 'deliberations' },
    { path: '/assessment/deliberations/:id', screen: 'deliberation' },
    { path: '/assessment/deliberations/:id/history', screen: 'deliberationHistory' },
    // D61 : les documents produits depuis une délibération close.
    { path: '/assessment/documents', screen: 'documents' },
    { path: '/assessment/documents/produce', screen: 'documentProduce' },
    { path: '/assessment/documents/settings', screen: 'documentSettings' },
    // D78 : la gestion des documents, son propre menu. Les anciennes adresses
    // ci-dessus restent servies (liens enregistrés, courriels).
    { path: '/documents', screen: 'documentDashboard' },
    // D80 : l'assistant de production, les lots, « À signer ».
    { path: '/documents/produce', screen: 'documentProduction' },
    { path: '/documents/jobs', screen: 'documentJobs' },
    { path: '/documents/jobs/:id', screen: 'documentJobs' },
    { path: '/documents/sign', screen: 'documentSign' },
    // D81 : les documents partagés ; leur lecture hors du portail étudiant.
    { path: '/documents/shared', screen: 'documentShared' },
    { path: '/documents/shared/:id', screen: 'documentShared' },
    { path: '/me/shared', screen: 'mySharedDocuments' },
    { path: '/documents/produce/single', screen: 'documentProduce' },
    { path: '/documents/registry', screen: 'documentRegistry' },
    { path: '/documents/registry/:id', screen: 'documentItem' },
    { path: '/documents/settings', screen: 'documentSettings' },
    // D79 : les modèles, leur éditeur, les modèles par formation.
    { path: '/documents/templates', screen: 'documentTemplates' },
    { path: '/documents/templates/trainings', screen: 'documentAssignments' },
    { path: '/documents/templates/:id', screen: 'documentTemplate' },
    // D59 : la traçabilité des consultations, dans la console.
    { path: '/super-administration/trace', screen: 'traceSettings' },
    // D62 : la planification. L'emploi du temps (une classe, un enseignant, une
    // salle, un élève), celui de l'enseignant connecté, le calendrier, les
    // salles, les heures.
    { path: '/planning/timetable', screen: 'timetable' },
    { path: '/planning/mine', screen: 'myTimetable' },
    { path: '/planning/calendar', screen: 'planningCalendar' },
    { path: '/planning/rooms', screen: 'rooms' },
    { path: '/planning/hours', screen: 'planningHours' },
    // D63 : l'assiduité. L'appel d'une séance s'ouvre aussi depuis l'emploi du
    // temps ou la semaine d'une classe.
    { path: '/attendance/dashboard', screen: 'attendanceDashboard' },
    { path: '/attendance/roll-call', screen: 'rollCall' },
    { path: '/attendance/roll-call/:id', screen: 'rollCall' },
    { path: '/attendance/absences', screen: 'absences' },
    // D65 : la gestion des enseignants. La fiche s'ouvre depuis la liste, ses
    // onglets vivent dans l'adresse (`?tab=`).
    { path: '/faculty/teachers', screen: 'facultyTeachers' },
    { path: '/faculty/teachers/new', screen: 'facultyTeacherCreate' },
    { path: '/faculty/teachers/:id', screen: 'facultyTeacher' },
    { path: '/faculty/contracts', screen: 'facultyContracts' },
    { path: '/faculty/settings', screen: 'facultySettings' },
    // D66 : la paie des enseignants (module optionnel). `/payroll` mène au mois courant.
    { path: '/payroll', screen: 'payroll' },
    { path: '/payroll/:month', screen: 'payrollMonth' },
    // D67 : la communication. La messagerie (une conversation ouverte a son
    // adresse, c'est aussi la vue « fil » du téléphone), les notifications aux
    // étudiants et leur suivi, les réglages. `/notifications` est l'accueil
    // d'un étudiant.
    { path: '/messages', screen: 'messages' },
    { path: '/messages/:id', screen: 'messages' },
    { path: '/notifications', screen: 'notifications' },
    { path: '/notifications/new', screen: 'notificationNew' },
    { path: '/notifications/:id', screen: 'notification' },
    { path: '/notifications/:id/edit', screen: 'notificationEdit' },
    { path: '/communication/settings', screen: 'communicationSettings' },
    // D68 : les relations extérieures (module optionnel). La fiche d'une
    // organisation et une convention s'ouvrent depuis les listes ; une
    // convention est aussi la cible de l'alerte « convention à échéance ».
    { path: '/relations', screen: 'relationsDashboard' },
    { path: '/relations/organisations', screen: 'relationsOrganisations' },
    { path: '/relations/organisations/:id', screen: 'relationsOrganisation' },
    { path: '/relations/agreements', screen: 'relationsAgreements' },
    { path: '/relations/agreements/:id', screen: 'relationsAgreement' },
    { path: '/relations/prospects', screen: 'relationsProspects' },
    { path: '/relations/alumni', screen: 'relationsAlumni' },
    { path: '/relations/settings', screen: 'relationsSettings' },
    // D69 : les stages (module optionnel, avec les Relations extérieures).
    // `/stages/mine` est l'espace de l'étudiant, `/stages/supervised` celui de
    // l'encadrant ; la fiche d'un stage est commune aux trois.
    { path: '/stages', screen: 'stagesDashboard' },
    { path: '/stages/internships', screen: 'stagesInternships' },
    { path: '/stages/internships/:id', screen: 'stagesInternship' },
    { path: '/stages/offers', screen: 'stagesOffers' },
    { path: '/stages/offers/:id', screen: 'stagesOffer' },
    { path: '/stages/defenses', screen: 'stagesDefenses' },
    { path: '/stages/settings', screen: 'stagesSettings' },
    { path: '/stages/mine', screen: 'stagesMine' },
    { path: '/stages/mine/offers', screen: 'stagesMyOffers' },
    { path: '/stages/supervised', screen: 'stagesSupervised' },
    // D71 : les demandes (module optionnel). `/requests/mine` est l'espace du
    // demandeur ; la fiche d'une demande sert le personnel et le demandeur.
    { path: '/requests', screen: 'requestsDashboard' },
    { path: '/requests/list', screen: 'requestsList' },
    { path: '/requests/settings', screen: 'requestsSettings' },
    { path: '/requests/mine', screen: 'requestsMine' },
    { path: '/requests/:id', screen: 'request' },
    // D72 : l'internat et l'hébergement (module optionnel, menu réduit à huit
    // entrées). `/housing/mine` est le portail de l'étudiant.
    { path: '/housing', screen: 'housingDashboard' },
    { path: '/housing/campaigns', screen: 'housingCampaigns' },
    { path: '/housing/campaigns/:id', screen: 'housingCampaign' },
    { path: '/housing/applications/:id', screen: 'housingApplication' },
    { path: '/housing/payments', screen: 'housingPayments' },
    { path: '/housing/stays', screen: 'housingStays' },
    { path: '/housing/upkeep', screen: 'housingUpkeep' },
    { path: '/housing/inspections/:id', screen: 'housingInspection' },
    { path: '/housing/repairs/:id', screen: 'housingRepair' },
    { path: '/housing/estate', screen: 'housingEstate' },
    { path: '/housing/rooms/:id', screen: 'housingRoom' },
    { path: '/housing/settings', screen: 'housingSettings' },
    { path: '/housing/mine', screen: 'housingMine' },
    // D82 : la bibliothèque. `/library/mine` et `/library/opac` sont le portail du lecteur.
    { path: '/library', screen: 'libraryDashboard' },
    { path: '/library/desk', screen: 'libraryDesk' },
    { path: '/library/catalog', screen: 'libraryCatalog' },
    { path: '/library/editions/new', screen: 'libraryEditionNew' },
    { path: '/library/editions/:id/edit', screen: 'libraryEditionEdit' },
    { path: '/library/editions/:id', screen: 'libraryEdition' },
    { path: '/library/items/:id', screen: 'libraryItem' },
    { path: '/library/authorities', screen: 'libraryAuthorities' },
    { path: '/library/locations', screen: 'libraryLocations' },
    { path: '/library/readers', screen: 'libraryReaders' },
    { path: '/library/readers/:id', screen: 'libraryReader' },
    { path: '/library/holds', screen: 'libraryHolds' },
    { path: '/library/overdue', screen: 'libraryOverdue' },
    { path: '/library/inventories', screen: 'libraryInventories' },
    { path: '/library/inventories/:id', screen: 'libraryInventory' },
    { path: '/library/inventories/:id/scan', screen: 'libraryInventoryScan' },
    { path: '/library/imports', screen: 'libraryImports' },
    { path: '/library/imports/:id', screen: 'libraryImport' },
    { path: '/library/settings', screen: 'librarySettings' },
    { path: '/library/stats', screen: 'libraryStats' },
    { path: '/library/suggestions', screen: 'librarySuggestions' },
    { path: '/library/mine', screen: 'libraryMine' },
    { path: '/library/opac', screen: 'libraryOpac' },
    { path: '/library/opac/:id', screen: 'libraryOpacEdition' },
    // D73 : « Mon espace », le portail de l'étudiant ; ses réglages sont
    // dans l'administration.
    { path: '/me', screen: 'portalHome' },
    { path: '/me/timetable', screen: 'portalTimetable' },
    { path: '/me/marks', screen: 'portalMarks' },
    { path: '/me/attendance', screen: 'portalAttendance' },
    { path: '/me/documents', screen: 'portalDocuments' },
    { path: '/me/finance', screen: 'portalFinance' },
    { path: '/me/profile', screen: 'portalProfile' },
    { path: '/administration/portal', screen: 'portalSettings' },
    // D77 : l'identité de l'établissement.
    { path: '/administration/identity', screen: 'brandingIdentity' },
    { path: '/administration/identity/history', screen: 'brandingHistory' },
    // D73, suite : l'espace de l'enseignant.
    { path: '/teach', screen: 'teacherHome' },
    { path: '/teach/rolls', screen: 'teacherRolls' },
    { path: '/teach/classes', screen: 'teacherClasses' },
    { path: '/teach/hours', screen: 'teacherHours' },
    { path: '/teach/pay', screen: 'teacherPay' },
    { path: '/teach/profile', screen: 'teacherProfile' },
    // D74 : le suivi des compétences (la pédagogie, l'enseignant, l'étudiant).
    { path: '/skills/frameworks', screen: 'skillsFrameworks' },
    { path: '/skills/frameworks/new', screen: 'skillsFramework' },
    { path: '/skills/frameworks/:id', screen: 'skillsFramework' },
    { path: '/skills/activities', screen: 'skillsActivities' },
    { path: '/skills/activities/:id', screen: 'skillsGrid' },
    { path: '/skills/students/:studentId/:skillId', screen: 'skillsHistory' },
    { path: '/skills/report', screen: 'skillsReport' },
    { path: '/skills/settings', screen: 'skillsSettings' },
    { path: '/teach/skills', screen: 'teacherSkills' },
    { path: '/me/skills', screen: 'skillsMine' },
    { path: '/me/skills/:skillId', screen: 'skillsMineSkill' },
    // D76 : l'E-Learning (l'enseignant, l'étudiant, la coordination et la scolarité).
    { path: '/teach/courses', screen: 'learningTeach' },
    { path: '/teach/courses/:subjectId', screen: 'learningCourse' },
    { path: '/teach/courses/:subjectId/bank', screen: 'learningBank' },
    { path: '/teach/courses/:subjectId/questions/new', screen: 'learningQuestion' },
    { path: '/teach/questions/:id', screen: 'learningQuestion' },
    { path: '/teach/resources/:id/views', screen: 'learningResourceViews' },
    { path: '/teach/quizzes/new', screen: 'learningQuiz' },
    { path: '/teach/quizzes/:id', screen: 'learningQuiz' },
    { path: '/teach/quizzes/:id/copies', screen: 'learningCopies' },
    { path: '/teach/quizzes/:id/grading', screen: 'learningGrading' },
    { path: '/teach/attempts/:id', screen: 'learningAttempt' },
    { path: '/me/courses', screen: 'learningMine' },
    { path: '/me/courses/:spaceId', screen: 'learningMineCourse' },
    { path: '/me/attempts/:id', screen: 'learningPlay' },
    { path: '/learning', screen: 'learningDashboard' },
    { path: '/learning/plans', screen: 'learningPlans' },
    { path: '/learning/subjects', screen: 'learningSubjects' },
    { path: '/learning/transfers', screen: 'learningTransfers' },
    { path: '/learning/accommodations', screen: 'learningAccommodations' },
    { path: '/learning/settings', screen: 'learningSettings' },
] as const satisfies readonly RouteDeclaration[];

/** Les clés d'écran réellement déclarées. `screens.tsx` doit toutes les couvrir. */
export type ScreenKey = (typeof ROUTES)[number]['screen'];

/**
 * Les routes PUBLIQUES, servies hors de la coquille et sans session. Elles ne
 * figurent pas dans ROUTES : aucune entrée de menu ne doit pouvoir y mener, et
 * le manifeste de D17 ne les contient donc pas.
 *
 * `/password/set` est l'adresse que porte le courriel d'activation ou de
 * réinitialisation (PasswordLinkMailer) : la changer ici sans la changer là-bas
 * casserait tous les liens déjà envoyés.
 */
export const PUBLIC_ROUTES = {
    forgotPassword: '/password/forgot',
    setPassword: '/password/set',
    // D61 : l'adresse que porte le code QR des documents produits
    // (DocumentRenderer::verifyUrl). La changer casse les codes déjà imprimés.
    verifyDocument: '/verify',
    // D68 : l'adresse que porte le courriel de l'enquête d'insertion
    // (`{front}/insertion/{token}`). La changer casse les liens déjà envoyés.
    insertionSurvey: '/insertion',
} as const;
